<template>
  <div v-if="isOpen" class="fixed inset-0 bg-gray-900 bg-opacity-70 flex items-center justify-center p-4 z-50 animate-modalFade">
    <div class="w-full max-w-md bg-gray-800 text-indigo-200 rounded-lg p-4 sm:p-5 flex flex-col gap-4 relative shadow-lg border border-gray-800 animate-scaleIn">
      <!-- Header -->
      <div class="flex justify-between items-center">
        <h2 class="text-lg font-semibold text-indigo-200">แผนที่ของฉัน</h2>
        <button
          @click="closeModal"
          class="text-indigo-200 hover:text-indigo-300 transition-colors duration-200"
          aria-label="ปิด"
        >
          <i class="fas fa-times text-sm"></i>
        </button>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center">
        <div class="animate-spin rounded-full h-6 w-6 border-t-2 border-indigo-600 mx-auto"></div>
        <p class="text-xs mt-1">กำลังโหลด...</p>
      </div>

      <!-- Empty -->
      <div v-else-if="maps.length === 0" class="text-center text-gray-300 py-4 text-xs sm:text-sm">
        ไม่พบข้อมูลแผนที่ที่ปลดล็อค
      </div>

      <!-- Map List -->
      <div
        v-else
        ref="mapList"
        class="grid grid-cols-1 gap-2 max-h-72 overflow-y-auto scrollbar-thin"
      >
        <div
          v-for="map in maps"
          :key="map.id"
          class="bg-gray-900 border rounded-md p-2 sm:p-3 flex items-center gap-3 transition-all duration-200 hover:border-indigo-700"
          :class="unlockedMaps.includes(map.requiredMap) ? 'border-green-500' : 'border-gray-700'"
        >
          <img
            :src="map.imageUrl || 'default-map.png'"
            alt="Map"
            class="w-12 h-12 rounded-md object-cover border border-gray-700"
            @error="handleImageError"
          />
          <div class="flex flex-col flex-grow min-w-0">
            <span class="font-medium text-indigo-200 truncate">{{ map.name }}</span>
            <span class="text-xxs sm:text-xs text-gray-400">เลเวล: {{ map.requiredLevel || 0 }}</span>
          </div>
          <span v-if="unlockedMaps.includes(map.name)" class="text-green-400 flex-shrink-0">
            <i class="fas fa-check-circle"></i>
          </span>
        </div>
      </div>

      <!-- Error -->
      <div v-if="errorMessage" class="text-red-300 text-xs sm:text-sm text-center">
        {{ errorMessage }}
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { projectFirestore } from '@/firebase/config';
import getUser from '@/composables/getUser';
import { usePlayerStore } from '@/composables/usePlayerStore';

export default {
  name: 'MapsModal',
  props: {
    isOpen: { type: Boolean, default: false }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const { user } = getUser();
    const playerStore = usePlayerStore();
    const unlockedMaps = ref([]);
    const maps = ref([]);
    const errorMessage = ref('');
    const loading = ref(true);
    const mapList = ref(null);
    const isAdmin = computed(() => playerStore.userData?.admin === true);

    const handleImageError = (event) => {
      event.target.src = 'default-map.png';
    };

    const fetchData = async () => {
      if (!user.value) {
        errorMessage.value = 'กรุณาล็อกอินก่อน';
        loading.value = false;
        return;
      }
      try {
        const charPrivateDoc = await projectFirestore
          .collection('characterPrivate')
          .doc(user.value.uid)
          .get();
        if (charPrivateDoc.exists) {
          unlockedMaps.value = charPrivateDoc.data().unlockedMaps || [];
        } else {
          errorMessage.value = 'ไม่พบข้อมูลส่วนตัวของตัวละคร';
          loading.value = false;
          return;
        }

        let mapsRef = projectFirestore.collection('maps').where('requiredMap', '!=', '');
        if (!isAdmin.value) mapsRef = mapsRef.where('isActive', '==', true);

        const mapsSnapshot = await mapsRef.get();
        let fetchedMaps = mapsSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        fetchedMaps.sort((a, b) => (a.requiredLevel || 0) - (b.requiredLevel || 0));
        maps.value = fetchedMaps;
      } catch {
        errorMessage.value = 'ไม่สามารถโหลดข้อมูลได้';
      } finally {
        loading.value = false;
      }
    };

    const closeModal = () => {
      errorMessage.value = '';
      emit('close');
    };

    watch(
      () => props.isOpen,
      (val) => {
        if (val) {
          loading.value = true;
          fetchData();
        }
      }
    );

    onMounted(() => {
      if (props.isOpen) fetchData();
    });

    return {
      unlockedMaps,
      maps,
      loading,
      errorMessage,
      closeModal,
      mapList,
      handleImageError
    };
  }
};
</script>

<style scoped>
.scrollbar-thin::-webkit-scrollbar {
  width: 5px;
}
.scrollbar-thin::-webkit-scrollbar-track {
  background: #2d3748;
  border-radius: 10px;
}
.scrollbar-thin::-webkit-scrollbar-thumb {
  background: #4a5568;
  border-radius: 10px;
}
.scrollbar-thin::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}

@keyframes modalFade {
  from { opacity: 0; }
  to { opacity: 1; }
}
.animate-modalFade {
  animation: modalFade 0.3s ease-out;
}

@keyframes scaleIn {
  from { transform: scale(0.95); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}
.animate-scaleIn {
  animation: scaleIn 0.3s ease-out;
}

.animate-spin {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.text-xxs {
  font-size: 0.7rem;
  line-height: 1rem;
}
</style>
